/**
 * Forecast evolution on the stock Dividend tab (TED #2456, ortex-gui#1262): the ladder's small
 * chart of how a past forecast moved to the declared amount, and the drawer with one event's whole
 * path (opened from a ladder row). The track record tile is `TrackRecordBox` in `Boxes.tsx`.
 *
 * Gate-aware: TED sends no spark, no points and no track record where this listing's forecasts are
 * not published (staff see them), and dates without amounts below Advanced; each part renders only
 * what it was sent.
 */
import { CSSProperties, FC, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Drawer, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAtomValue } from "jotai";
import { backgroundColorState } from "@/lib/store";
import { getDivEventEvolution } from "@/controllers/dividends-v2";
import { DivEvolutionPoint, DivEvolutionResponse, DivEvolutionSpark, DivLadderRow, DivStockResponse } from "@/models/dividends-v2";
import { bandPath, demoEvolution, linePath, ORIGIN_HELP, ORIGIN_LABEL, pathScale, settledSentence } from "./evolutionData";
import { fmtDate, fmtDateShort, fmtMoney, fmtProb, isNum } from "./format";
import styles from "./StockDividendTab.module.scss";

// ─── The ladder's spark ────────────────────────────────────────────────────────

const SPARK_W = 76;
const SPARK_H = 22;

/** A past ladder row's path: the central forecast at each frozen date, the declared amount dashed. */
export const EvolutionSpark: FC<{ spark: DivEvolutionSpark | null | undefined; label: string; currency?: string | null }> = ({ spark, label, currency = null }) => {
    const geometry = useMemo(() => {
        if (!spark || spark.points.length < 1) return null;
        const pts = spark.points.map((p) => ({ as_of: p.as_of, values: [p.amount_p50] }));
        const end = spark.points[spark.points.length - 1].as_of;
        const scale = pathScale(pts, end, [spark.declared_amount], SPARK_W, SPARK_H, 3);
        if (!scale) return null;
        const xy = spark.points.map((p) => [scale.x(p.as_of), isNum(p.amount_p50) ? scale.y(p.amount_p50) : null] as [number, number | null]);
        return { scale, xy };
    }, [spark]);
    if (!spark || !geometry) return null;
    const { scale, xy } = geometry;
    const last = xy[xy.length - 1];
    const settled = !!spark.amount_exact_since;
    const sentence = settledSentence(spark, currency);
    return (
        <svg className={styles.evoSpark} width={SPARK_W} height={SPARK_H} viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} role="img" aria-label={`${label}: ${sentence ?? "forecast path"}`}>
            <title>{sentence ?? "How the forecast moved"}</title>
            {isNum(spark.declared_amount) && <line className={styles.evoDeclared} x1={0} x2={SPARK_W} y1={scale.y(spark.declared_amount)} y2={scale.y(spark.declared_amount)} />}
            <path className={styles.evoLine} d={linePath(xy)} />
            {last[1] !== null && <circle className={settled ? styles.evoDotOk : styles.evoDotMiss} cx={last[0]} cy={last[1]} r={2.4} />}
        </svg>
    );
};

// ─── The drawer ────────────────────────────────────────────────────────────────

const CHART_W = 520;
const CHART_H = 200;

/** The path chart: the p10..p90 band, the central forecast (hollow dots replayed, filled published), the declared amount and the announcement. */
const PathChart: FC<{ data: DivEvolutionResponse }> = ({ data }) => {
    const pts = data.points.filter((p) => isNum(p.amount_p50));
    const declared = data.declaration?.amount ?? null;
    const announced = data.declaration?.announced_on ?? null;
    const scale = pathScale(
        pts.map((p) => ({ as_of: p.as_of, values: [p.amount_p10, p.amount_p50, p.amount_p90] })),
        announced,
        [declared],
        CHART_W,
        CHART_H,
        14
    );
    if (!scale || !pts.length) return null;
    const xs = pts.map((p) => scale.x(p.as_of));
    const band = bandPath(pts.map((p, i) => ({ x: xs[i], lo: isNum(p.amount_p10) ? scale.y(p.amount_p10) : null, hi: isNum(p.amount_p90) ? scale.y(p.amount_p90) : null })));
    const line = linePath(pts.map((p, i) => [xs[i], scale.y(p.amount_p50 as number)]));
    const ticks = [scale.min + (scale.max - scale.min) * 0.15, (scale.min + scale.max) / 2, scale.max - (scale.max - scale.min) * 0.15];
    return (
        <svg className={styles.evoChart} viewBox={`0 0 ${CHART_W} ${CHART_H + 18}`} role="img" aria-label={`Forecast path of ${data.period_label ?? data.event_key}`}>
            {ticks.map((t) => (
                <g key={t}>
                    <line className={styles.evoGrid} x1={0} x2={CHART_W} y1={scale.y(t)} y2={scale.y(t)} />
                    <text className={styles.evoAxis} x={2} y={scale.y(t) - 3}>
                        {t.toFixed(Math.abs(t) < 10 ? 3 : 2)}
                    </text>
                </g>
            ))}
            {band && <path className={styles.evoBand} d={band} />}
            {isNum(declared) && (
                <g>
                    <line className={styles.evoDeclared} x1={0} x2={CHART_W} y1={scale.y(declared)} y2={scale.y(declared)} />
                    <text className={styles.evoDeclaredLabel} x={CHART_W - 4} y={scale.y(declared) - 4} textAnchor="end">
                        Declared {fmtMoney(declared, data.currency)}
                    </text>
                </g>
            )}
            {announced && (
                <g>
                    <line className={styles.evoMarker} x1={scale.x(announced)} x2={scale.x(announced)} y1={6} y2={CHART_H - 6} />
                    <text className={styles.evoAxis} x={scale.x(announced) - 3} y={CHART_H + 12} textAnchor="end">
                        declared {fmtDateShort(announced)}
                    </text>
                </g>
            )}
            <path className={styles.evoLine} d={line} />
            {pts.map((p, i) => (
                <circle key={p.as_of} className={p.origin === "published" ? styles.evoDotPublished : styles.evoDotReplayed} cx={xs[i]} cy={scale.y(p.amount_p50 as number)} r={3.2}>
                    <title>
                        {fmtDate(p.as_of, true)}: {fmtMoney(p.amount_p50, data.currency)} ({fmtMoney(p.amount_p10, data.currency)}–{fmtMoney(p.amount_p90, data.currency)}), {ORIGIN_LABEL[p.origin]}
                    </title>
                </circle>
            ))}
            <text className={styles.evoAxis} x={xs[0]} y={CHART_H + 12}>
                {fmtDateShort(pts[0].as_of)}
            </text>
        </svg>
    );
};

const HORIZON_WORDS: Record<string, string> = { first: "First", d90: "90 days before", d30: "30 days before", d7: "7 days before", d1: "1 day before", latest: "Today" };

const PointRow: FC<{ p: DivEvolutionPoint; declaredEx: string | null; locked: boolean; currency: string | null }> = ({ p, declaredEx, locked, currency }) => {
    const money = (v: number | null | undefined) => fmtMoney(v, currency);
    const range = money(p.amount_p10) && money(p.amount_p90) ? `${money(p.amount_p10)}–${money(p.amount_p90)}` : null;
    const cut = isNum(p.p_cut) || isNum(p.p_omit) ? (p.p_cut ?? 0) + (p.p_omit ?? 0) : null;
    const exHit = declaredEx && p.ex_date ? p.ex_date === declaredEx : null;
    return (
        <tr>
            <td>{fmtDateShort(p.as_of)}</td>
            <td>{p.horizon ? (HORIZON_WORDS[p.horizon] ?? p.horizon) : "Daily"}</td>
            <td className={styles.num}>{locked ? "·" : (money(p.amount_p50) ?? "n/a")}</td>
            <td className={`${styles.num} ${styles.band}`}>{locked ? "" : range}</td>
            <td>
                {fmtDateShort(p.ex_date) ?? "n/a"}
                {exHit === true && <span className={styles.ok}> ✓</span>}
            </td>
            <td className={styles.num}>{locked ? "·" : (fmtProb(cut) ?? "n/a")}</td>
            <td title={ORIGIN_HELP[p.origin]}>
                <span className={p.origin === "published" ? styles.evoTagPublished : styles.evoTagReplayed}>{ORIGIN_LABEL[p.origin]}</span>
            </td>
        </tr>
    );
};

type DrawerState = { loading: boolean; data: DivEvolutionResponse | null; error: string | null };

/** One event's path, read when the drawer opens (the demo builds it from the ladder row). */
const useEvolution = (payload: DivStockResponse, row: DivLadderRow | null, demo: boolean): DrawerState => {
    const [state, setState] = useState<DrawerState>({ loading: false, data: null, error: null });
    useEffect(() => {
        if (!row) return undefined;
        if (demo) {
            const data = demoEvolution(payload, row);
            const locked = payload.locked;
            const gated = payload.published === false ? { ...data, points: [], convergence: null, origin: null } : data;
            setState({
                loading: false,
                data: locked
                    ? {
                          ...gated,
                          points: gated.points.map((p) => ({ ...p, amount_p10: null, amount_p50: null, amount_p90: null, p_raise: null, p_hold: null, p_cut: null, p_omit: null, p_special: null })),
                          declaration: gated.declaration ? { ...gated.declaration, amount: null } : null,
                      }
                    : gated,
                error: null,
            });
            return undefined;
        }
        const ac = new AbortController();
        setState({ loading: true, data: null, error: null });
        getDivEventEvolution(payload.tradingitem_id, row.event_key, ac.signal)
            .then((res) => {
                if (ac.signal.aborted) return;
                if (res.status === "ok" && res.data) setState({ loading: false, data: res.data, error: null });
                else if (res.status === "missing") setState({ loading: false, data: null, error: "The history of this forecast is not available yet." });
                else setState({ loading: false, data: null, error: "The history of this forecast could not be loaded. Try again in a moment." });
            })
            .catch((err) => {
                if (axios.isCancel(err) || ac.signal.aborted) return;
                setState({ loading: false, data: null, error: "The history of this forecast could not be loaded. Try again in a moment." });
            });
        return () => ac.abort();
    }, [payload, row, demo]);
    return state;
};

/** The drawer: the settled sentence, the path chart and every frozen forecast of the event. */
export const EvolutionDrawer: FC<{ payload: DivStockResponse; row: DivLadderRow | null; demo: boolean; themeVars: CSSProperties; onClose: () => void; onUnlock: () => void }> = ({ payload, row, demo, themeVars, onClose, onUnlock }) => {
    const background = useAtomValue(backgroundColorState);
    const state = useEvolution(payload, row, demo);
    const data = state.data;
    const locked = !!data?.locked;
    const sentence = data?.convergence && !locked ? settledSentence(data.convergence, data.currency) : null;
    const exLine =
        data?.convergence?.ex_exact_since && !locked
            ? `Ex-date ${fmtDate(data.convergence.declared_ex_date, true)} forecast from ${fmtDate(data.convergence.ex_exact_since, true)}${
                  isNum(data.convergence.ex_exact_days_before) ? `, ${data.convergence.ex_exact_days_before} days before the declaration` : ""
              }.`
            : data?.convergence?.ex_exact_since
              ? `Ex-date forecast exactly from ${fmtDate(data.convergence.ex_exact_since, true)}.`
              : null;
    const unpublished = !!data && data.points.length === 0 && data.published === false;
    const title = row ? `${payload.ticker ?? ""} · ${row.period_label ?? row.event_key}` : "";
    return (
        <Drawer
            anchor="right"
            open={!!row}
            onClose={onClose}
            slotProps={{
                paper: { sx: { background, width: { xs: "100vw", sm: 620 }, maxWidth: "100vw" }, role: "dialog", "aria-labelledby": "div-evolution-title" } as object,
            }}
        >
            <div className={styles.evoDrawer} style={themeVars}>
                <div className={styles.evoHead}>
                    <span id="div-evolution-title">How the forecast moved · {title}</span>
                    <IconButton size="small" aria-label="Close" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>
                {state.loading && <div className={styles.empty}>Loading the forecast history…</div>}
                {state.error && <div className={styles.empty}>{state.error}</div>}
                {unpublished && <div className={styles.empty}>The history of each forecast shows here once ORTEX publishes dividend forecasts for this market.</div>}
                {data && !unpublished && (
                    <>
                        <div className={styles.evoSummary}>
                            {data.declaration ? (
                                <div>
                                    <b>Declared</b> {locked ? "" : `${fmtMoney(data.declaration.amount, data.currency) ?? ""} · `}ex-date {fmtDate(data.declaration.ex_date, true)}
                                    {data.declaration.announced_on ? ` · announced ${fmtDate(data.declaration.announced_on, true)}` : ""}
                                </div>
                            ) : (
                                <div>
                                    <b>Not declared yet.</b> The path runs to today&apos;s forecast.
                                </div>
                            )}
                            {sentence && <div>{sentence}</div>}
                            {exLine && <div>{exLine}</div>}
                            {data.convergence && !locked && isNum(data.convergence.first_error_pct) && (
                                <div className={styles.dim}>
                                    First forecast {fmtMoney(data.convergence.first_amount, data.currency)} ({data.convergence.first_error_pct > 0 ? "+" : ""}
                                    {data.convergence.first_error_pct}% against the declared amount), {data.convergence.points} frozen forecasts.
                                </div>
                            )}
                        </div>
                        {!locked && <PathChart data={data} />}
                        {locked && (
                            <div className={styles.upsell}>
                                <span>
                                    <b>Dates are free.</b> The forecast amounts on this path are part of ORTEX Advanced.
                                </span>
                                <button type="button" className={styles.titleLink} onClick={onUnlock}>
                                    See plans
                                </button>
                            </div>
                        )}
                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Frozen</th>
                                        <th>When</th>
                                        <th className={styles.num}>Forecast</th>
                                        <th className={styles.num}>Range</th>
                                        <th>Ex-date</th>
                                        <th className={styles.num}>Cut</th>
                                        <th>Origin</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...data.points].reverse().map((p) => (
                                        <PointRow key={p.as_of} p={p} declaredEx={data.declaration?.ex_date ?? null} locked={locked} currency={data.currency ?? null} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className={styles.foot}>
                            Each forecast is frozen on its day and never revised. {ORIGIN_HELP.replayed} {ORIGIN_HELP.published}
                        </div>
                    </>
                )}
            </div>
        </Drawer>
    );
};
